/** @format */

/**
 * Span 详情抽屉：仅「详细信息」区块，内含基本信息 / LLM数据 / 属性 / 事件子 Tab。
 * 展开与关闭由父组件通过 visible、onClose 控制。
 */

import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  _$,
  Button,
  ButtonGroup,
  Icon,
  Tooltip,
  dayjs,
  Collapse,
  CollapseItem
} from '@king-design/react'
import { Descriptions, DescriptionItem } from 'src/components'
import { secondsFormat } from 'src/utils'
import type { TraceAnalysisDetailSpan } from '../typescript'
import TraceSpanLog from './TraceSpanLog'
import { renderStatusCode } from '../container/TraceTable'

export interface TraceSpanDetailProps {
  className?: string
  span: TraceAnalysisDetailSpan | null
  sessionId?: string
}

type DetailSubTab = 'basic' | 'attributes'

/** 将当前 Span 导出为 JSON 文件 */
function downloadSpanJson(span: TraceAnalysisDetailSpan) {
  const blob = new Blob([JSON.stringify(span, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `span-${span.SpanId}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}

const KvValueWithTooltip: React.FC<{ value?: string }> = ({ value }) => {
  const text = value || '-'
  const textRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const checkTruncated = () => {
      const el = textRef.current
      if (!el) {
        return
      }
      setIsTruncated(el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth)
    }
    checkTruncated()
    window.addEventListener('resize', checkTruncated)
    return () => window.removeEventListener('resize', checkTruncated)
  }, [text])

  const textNode = (
    <span ref={textRef} className="trace-span-detail-kv-value-text">
      {text}
    </span>
  )

  return isTruncated ? (
    <Tooltip content={text} theme="light">
      {textNode}
    </Tooltip>
  ) : (
    textNode
  )
}

const TraceSpanDetail: React.FC<TraceSpanDetailProps> = ({ className, span, sessionId }) => {
  // tab
  const [subTab, setSubTab] = useState<DetailSubTab>('basic')
  // 原始日志抽屉
  const [logDrawer, setLogDrawer] = useState(false)

  const renderBasicInfo = () => {
    if (!span) {
      return null
    }
    return (
      <Descriptions columns={2}>
        <DescriptionItem label={_$('Span 类型')}>{span.Kind || '-'}</DescriptionItem>
        <DescriptionItem label={_$('开始时间')}>
          {span.StartTime ? dayjs(span.StartTime).format('MM-DD HH:mm:ss') : '-'}
        </DescriptionItem>
        <DescriptionItem label={_$('结束时间')}>
          {span.StartTime ? dayjs(span.StartTime).format('MM-DD HH:mm:ss') : '-'}
        </DescriptionItem>
        <DescriptionItem label={_$('操作')}>{span.Name || '-'}</DescriptionItem>
        <DescriptionItem label={_$('耗时')}>{secondsFormat(span.DurationMs)}</DescriptionItem>
        <DescriptionItem label={_$('状态码')}>{renderStatusCode(span.StatusCode)}</DescriptionItem>
      </Descriptions>
    )
  }

  /** 属性列表：按 key 前缀分组展示 */
  const renderKeyValueList = (span: TraceAnalysisDetailSpan | undefined) => {
    if (!span || Object.keys(span.Attributes || {}).length === 0) {
      return <p className="trace-span-detail-empty">{_$('暂无属性')}</p>
    }
    const attributes = span.Attributes || {}
    const groupedRows = {
      genAi: [] as Array<[string, string]>,
      sdk: [] as Array<[string, string]>,
      langfuse: [] as Array<[string, string]>,
      other: [] as Array<[string, string]>
    }
    Object.entries(attributes).forEach(([key, value]) => {
      if (key.startsWith('gen_ai.')) {
        groupedRows.genAi.push([key, value])
      } else if (key.startsWith('sdk.')) {
        groupedRows.sdk.push([key, value])
      } else if (key.startsWith('langfuse.')) {
        groupedRows.langfuse.push([key, value])
      } else {
        groupedRows.other.push([key, value])
      }
    })

    const sections = [
      {
        key: 'basic',
        title: _$('基本信息'),
        rows: [
          ['ServiceName', span?.ServiceName || '-'],
          ['SessionId', span.Resource?.['session.id'] || sessionId]
        ]
      },
      { key: 'genAi', title: _$('Gen AI信息'), rows: groupedRows.genAi },
      { key: 'sdk', title: _$('SDK信息'), rows: groupedRows.sdk },
      { key: 'langfuse', title: _$('Langfuse信息'), rows: groupedRows.langfuse },
      { key: 'other', title: _$('其他信息'), rows: groupedRows.other }
    ].filter(section => section.rows.length > 0)
    return (
      <Collapse noBorder value={sections.map(section => section.key)}>
        {sections.map(section => (
          <CollapseItem key={section.key} value={section.key} title={section.title}>
            <dl className="trace-span-detail-kv-list">
              {section.rows.map(([key, value]) => (
                <div key={key} className="trace-span-detail-kv-item">
                  <dt className="trace-span-detail-kv-key" title={key}>
                    {key}
                  </dt>
                  <dd className="trace-span-detail-kv-value">
                    <KvValueWithTooltip value={value} />
                  </dd>
                </div>
              ))}
            </dl>
          </CollapseItem>
        ))}
      </Collapse>
    )
  }

  return (
    <div className={className}>
      <div>
        <div className="trace-span-detail-header">
          <p className="trace-span-detail-title" title={span?.Name ?? ''}>
            {span?.Name ?? '-'}
            <span className="trace-span-detail-title-id">{span?.SpanId ?? '-'}</span>
          </p>
          <div className="trace-span-detail-header-actions">
            <Icon className="ion-eye" size={18} onClick={() => setLogDrawer(true)} />
            <Icon
              className="ion-android-download"
              size={18}
              onClick={() => span && downloadSpanJson(span)}
            />
          </div>
        </div>
        <div className="trace-span-detail-content">
          <ButtonGroup
            className="trace-span-detail-sub-tabs"
            checkType="radio"
            value={subTab}
            onChangeValue={value => setSubTab(value as DetailSubTab)}
          >
            <Button value="basic">{_$('基本信息')}</Button>
            <Button value="attributes">{_$('属性')}</Button>
          </ButtonGroup>

          <div className="trace-span-detail-body">
            {subTab === 'basic'
              ? renderBasicInfo()
              : subTab === 'attributes'
              ? renderKeyValueList(span)
              : null}
          </div>
        </div>
      </div>
      <TraceSpanLog visible={logDrawer} onClose={() => setLogDrawer(false)} span={span} />
    </div>
  )
}

export default styled(TraceSpanDetail)`
  /* 抽屉头部 */
  .trace-span-detail-header {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding-right: 8px;
    background: #fafafa;
    height: 36px;
    border-bottom: 1px solid #f0f0f0;
    .trace-span-detail-title {
      margin: 0;
      font-size: 14px;
      font-weight: 500;
      padding-left: 16px;
      max-width: 800px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      .trace-span-detail-title-id {
        margin-left: 8px;
        color: #999;
        font-size: 12px;
        font-weight: 400;
      }
    }

    .trace-span-detail-header-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;

      .k-icon {
        cursor: pointer;
        &:hover {
          color: rgb(0, 145, 234);
          background: rgba(0, 145, 234, 0.06);
        }
      }
    }
  }

  .trace-span-detail-content {
    padding: 8px 16px;
  }
  /* 基本信息 / 属性 子 Tab */
  .trace-span-detail-sub-tabs {
    margin-bottom: 16px;
  }

  /* 抽屉正文 */
  .trace-span-detail-body {
    min-height: 120px;

    .k-collapse-item {
      border-bottom: none;
    }
    .trace-span-detail-empty {
      margin: 24px 0;
      text-align: center;
      color: rgba(0, 0, 0, 0.45);
      font-size: 13px;
    }

    .trace-span-detail-kv-list {
      margin: 0;
      padding: 0;
      list-style: none;
      border: 1px solid #f0f0f0;
      border-radius: 4px;
      overflow: hidden;

      .trace-span-detail-kv-item {
        display: flex;
        align-items: flex-start;
        gap: 0;
        margin: 0;
        padding: 10px 12px;
        border-bottom: 1px solid #f0f0f0;
        font-size: 13px;
        line-height: 22px;
        background: #fff;

        &:nth-child(even) {
          background: #fafafa;
        }

        &:last-child {
          border-bottom: none;
        }

        .trace-span-detail-kv-key {
          flex-shrink: 0;
          margin: 0;
          max-width: 42%;
          padding-right: 4px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 12px;
          font-weight: 500;
          color: rgba(0, 0, 0, 0.65);
          word-break: break-all;

          &::after {
            content: ':';
            margin-left: 2px;
            font-weight: 400;
            color: rgba(0, 0, 0, 0.35);
          }
        }

        .trace-span-detail-kv-value {
          flex: 1;
          min-width: 0;
          margin: 0;
          color: rgba(0, 0, 0, 0.88);
          .trace-span-detail-kv-value-text {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            word-break: break-all;
            white-space: pre-wrap;
          }
        }
      }
    }
  }
`
